import * as pc from 'playcanvas';

interface MatOptions {
  diffuse: pc.Color;
  emissive?: pc.Color;
  emissiveIntensity?: number;
  blendType?: number;
  depthWrite?: boolean;
}

class MaterialCache {
  private cache = new Map<string, pc.StandardMaterial>();

  /** Returns a shared cached material. Safe only when the material is never mutated after creation. */
  get(opts: MatOptions): pc.StandardMaterial {
    const key = this.makeKey(opts);
    if (this.cache.has(key)) return this.cache.get(key)!;
    const mat = this.build(opts);
    this.cache.set(key, mat);
    return mat;
  }

  /** Returns a unique (non-cached) material. Use when the material's properties are mutated at runtime. */
  createUnique(opts: MatOptions): pc.StandardMaterial {
    return this.build(opts);
  }

  private build(opts: MatOptions): pc.StandardMaterial {
    const mat = new pc.StandardMaterial();
    mat.diffuse = opts.diffuse.clone();
    const ei = opts.emissiveIntensity ?? 0;
    if (ei > 0 && opts.emissive) {
      mat.emissive = opts.emissive.clone();
      mat.emissiveIntensity = ei;
    }
    mat.blendType = opts.blendType ?? pc.BLEND_NONE;
    mat.depthWrite = opts.depthWrite ?? true;
    mat.update();
    return mat;
  }

  private makeKey(o: MatOptions): string {
    const r = (v: number) => Math.round(v * 1000);
    const d = o.diffuse;
    const e = o.emissive ?? new pc.Color(0, 0, 0);
    return `${r(d.r)},${r(d.g)},${r(d.b)}|${r(e.r)},${r(e.g)},${r(e.b)}|${r(o.emissiveIntensity ?? 0)}|${o.blendType ?? pc.BLEND_NONE}|${(o.depthWrite ?? true) ? 1 : 0}`;
  }
}

export const matCache = new MaterialCache();
